import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiX } from "react-icons/fi";
import {
  // DeletePhonemeDocument,
  GetPhonemesGroupedDocument,
  type PhonemeType,
  UpdatePhonemeDocument,
} from "../../../generated/graphql";
import { TagsInput } from "../TagsInput";
import { PHONEME_TYPES } from "./constants";

interface EditPhonemeModalProps {
  onClose: () => void;
  onSuccess: () => void;
  phonemeData: {
    id: string;
    phoneme: string;
    phonemeType: string;
    tags: string[];
  };
}

/**
 * Modal for editing phoneme metadata (tags, type)
 * Similar to MediaEditModal pattern
 */
export function EditPhonemeModal({ onClose, onSuccess, phonemeData }: EditPhonemeModalProps) {
  const [phoneme, setPhoneme] = useState("");
  const [phonemeType, setPhonemeType] = useState<string>("consonant");
  const [tags, setTags] = useState<string[]>([]);

  const [updatePhoneme, { loading }] = useMutation(UpdatePhonemeDocument, {
    // phoneme type null in variables, to match apollo normalization with the root query ( avoid re render )
    refetchQueries: [{ query: GetPhonemesGroupedDocument, variables: { phonemeType: null } }],
    onCompleted: () => {
      toast.success("Phoneme updated successfully");
      onSuccess();
    },
    onError: (error) => {
      toast.error(`Failed to update phoneme: ${error.message}`);
    },
  });

  // Initialize state from phonemeData
  useEffect(() => {
    setPhoneme(phonemeData.phoneme);
    setPhonemeType(phonemeData.phonemeType);
    setTags(phonemeData.tags || []);
  }, [phonemeData]);

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phoneme || !phonemeType) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await updatePhoneme({
        variables: {
          input: {
            id: phonemeData.id,
            phoneme: phoneme.toLowerCase().trim(),
            phonemeType: phonemeType as PhonemeType,
            tags: tags.length > 0 ? tags : [],
          },
        },
      });
    } catch (error) {
      console.error("Update phoneme failed:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Phoneme</h2>
            <p className="mt-1 text-sm text-gray-500">Update phoneme metadata and tags</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            disabled={loading}
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleUpdate} className="p-6">
          <div className="space-y-4">
            {/* Phoneme (readonly) */}
            <div>
              <label htmlFor="phoneme" className="block text-sm font-medium text-gray-700">
                Phoneme <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="phoneme"
                value={phoneme}
                onChange={(e) => setPhoneme(e.target.value)}
                placeholder="e.g., th, ch, a"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            {/* Phoneme Type */}
            <div>
              <label htmlFor="phonemeType" className="block text-sm font-medium text-gray-700">
                Phoneme Type <span className="text-red-500">*</span>
              </label>
              <select
                id="phonemeType"
                value={phonemeType}
                onChange={(e) => setPhonemeType(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                required
                disabled={loading}
              >
                {PHONEME_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
                Tags
              </label>
              <div className="mt-1">
                <TagsInput
                  value={tags}
                  onChange={setTags}
                  placeholder="voiced, unvoiced, etc."
                  disabled={loading}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Press Enter or comma to add tags</p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
